import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Grid,
  Chip,
  FormHelperText,
  Divider,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { formatDurationFromMinutes } from '../../../utils';

// Límites impuestos por el firmware (1-255 minutos por ciclo = 4 h 15 min)
export const MIN_CYCLE_MINUTES = 1;
export const MAX_CYCLE_MINUTES = 255;

const MAX_HOURS = Math.floor(MAX_CYCLE_MINUTES / 60);          // 4
const MAX_MINUTES_AT_MAX_HOURS = MAX_CYCLE_MINUTES % 60;       // 15

// Valores propuestos cuando todavía no hay un ciclo configurado
const DEFAULT_MINUTES_ON = 30;
const DEFAULT_MINUTES_OFF = 120;

// Atajos de un clic para los tiempos más habituales
const PRESETS_ON = [5, 10, 15, 30, 45, 60, 90, 120];
const PRESETS_OFF = [15, 30, 60, 90, 120, 180, 240];

const useStyles = makeStyles((theme) => ({
  description: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  notConfiguredAlert: {
    marginBottom: theme.spacing(1),
  },
  picker: {
    marginTop: theme.spacing(2),
  },
  presets: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  presetChip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  equivalence: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  preview: {
    marginTop: theme.spacing(2),
  },
  previewDetail: {
    display: 'block',
    marginTop: theme.spacing(1),
  },
  statusAlert: {
    marginTop: theme.spacing(2),
  },
}));

// Valida que el valor sea un entero dentro del rango permitido por el firmware
export const isValidCycleMinutes = (value) => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) &&
    parsedValue >= MIN_CYCLE_MINUTES &&
    parsedValue <= MAX_CYCLE_MINUTES;
};

/**
 * Selector de duración en horas y minutos. Hacia afuera siempre trabaja con el
 * total de minutos, que es lo que espera el comando MQTT.
 */
const DurationPicker = ({ idPrefix, label, presets, totalMinutes, onChange, disabled, error }) => {
  const classes = useStyles();

  const hours = Math.floor((Number(totalMinutes) || 0) / 60);
  const minutes = (Number(totalMinutes) || 0) % 60;

  // El campo de minutos guarda su propio texto para no estorbar mientras se escribe
  const [minutesText, setMinutesText] = useState(String(minutes));

  // Sincroniza el campo cuando el total cambia desde fuera (presets, precarga)
  useEffect(() => {
    const derivedMinutes = (Number(totalMinutes) || 0) % 60;
    setMinutesText((prevText) => (
      prevText !== '' && Number(prevText) === derivedMinutes ? prevText : String(derivedMinutes)
    ));
  }, [totalMinutes]);

  const handleHoursChange = (event) => {
    const nextHours = Number(event.target.value);
    // Con 4 h solo caben 15 minutos más antes de pasarse del tope del firmware
    const nextMinutes = nextHours === MAX_HOURS
      ? Math.min(minutes, MAX_MINUTES_AT_MAX_HOURS)
      : minutes;
    onChange(nextHours * 60 + nextMinutes);
  };

  const handleMinutesChange = (event) => {
    const { value } = event.target;

    if (value !== '') {
      if (!/^\d{1,2}$/.test(value)) return;
      if (Number(value) > 59) return;
    }

    setMinutesText(value);
    onChange(hours * 60 + (value === '' ? 0 : Number(value)));
  };

  const maxMinutesForHours = hours === MAX_HOURS ? MAX_MINUTES_AT_MAX_HOURS : 59;

  return (
    <div className={classes.picker}>
      <Typography variant="subtitle2">{label}</Typography>

      <div className={classes.presets}>
        {presets.map((preset) => (
          <Chip
            key={`${idPrefix}-preset-${preset}`}
            className={classes.presetChip}
            label={formatDurationFromMinutes(preset)}
            size="small"
            clickable={!disabled}
            disabled={disabled}
            color={Number(totalMinutes) === preset ? "primary" : "default"}
            variant={Number(totalMinutes) === preset ? "default" : "outlined"}
            onClick={() => onChange(preset)}
          />
        ))}
      </div>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <TextField
            id={`${idPrefix}-hours`}
            label="Horas"
            select
            fullWidth
            variant="outlined"
            size="small"
            value={hours}
            onChange={handleHoursChange}
            disabled={disabled}
            error={error}
          >
            {Array.from({ length: MAX_HOURS + 1 }, (unused, hourOption) => (
              <MenuItem key={`${idPrefix}-hour-${hourOption}`} value={hourOption}>
                {hourOption}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6} sm={4}>
          <TextField
            id={`${idPrefix}-minutes`}
            label="Minutos"
            type="number"
            fullWidth
            variant="outlined"
            size="small"
            value={minutesText}
            onChange={handleMinutesChange}
            disabled={disabled}
            error={error}
            helperText={hours === MAX_HOURS ? `Máximo ${MAX_MINUTES_AT_MAX_HOURS} min` : ''}
            InputProps={{ inputProps: { min: 0, max: maxMinutesForHours } }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <div className={classes.equivalence}>
            <Typography variant="body2" color="textSecondary">
              {isValidCycleMinutes(totalMinutes) ? `= ${totalMinutes} minutos` : ''}
            </Typography>
          </div>
        </Grid>
      </Grid>

      {error && (
        <FormHelperText error>
          Elija un tiempo entre {MIN_CYCLE_MINUTES} minuto y {formatDurationFromMinutes(MAX_CYCLE_MINUTES)}.
        </FormHelperText>
      )}
    </div>
  );
};

const RecirculationCycleDialog = ({ open, onClose, initialValues, status, onSubmit }) => {
  const classes = useStyles();

  const [minutesOn, setMinutesOn] = useState(DEFAULT_MINUTES_ON);
  const [minutesOff, setMinutesOff] = useState(DEFAULT_MINUTES_OFF);

  const isSending = status.state === 'sending';
  const hasFailed = status.state === 'error';
  const isConfigured = !!initialValues.configured;

  // Al abrir se precarga lo guardado; si aún no hay nada se proponen valores válidos
  useEffect(() => {
    if (open) {
      setMinutesOn(isValidCycleMinutes(initialValues.minutesOn)
        ? Number(initialValues.minutesOn)
        : DEFAULT_MINUTES_ON);
      setMinutesOff(isValidCycleMinutes(initialValues.minutesOff)
        ? Number(initialValues.minutesOff)
        : DEFAULT_MINUTES_OFF);
    }
  }, [open, initialValues.minutesOn, initialValues.minutesOff]);

  const isValid = isValidCycleMinutes(minutesOn) && isValidCycleMinutes(minutesOff);

  // Como siempre se parte de valores válidos, un tiempo inválido solo puede venir del usuario
  const showOnError = !isValidCycleMinutes(minutesOn);
  const showOffError = !isValidCycleMinutes(minutesOff);

  // Sin cambios el botón pasa a "Reenviar al módulo": permite resincronizar
  // el módulo con lo que ya está guardado (por ejemplo tras un reinicio)
  const isDirty = !isConfigured ||
    minutesOn !== Number(initialValues.minutesOn) ||
    minutesOff !== Number(initialValues.minutesOff);

  const totalCycleMinutes = minutesOn + minutesOff;
  const dutyCycle = totalCycleMinutes > 0
    ? Math.round((minutesOn / totalCycleMinutes) * 100)
    : 0;

  const handleClose = () => {
    if (isSending) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!isValid || isSending) return;
    onSubmit({ minutesOn, minutesOff });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={isSending}
      disableEscapeKeyDown={isSending}
      aria-labelledby="recirculation-cycle-dialog-title"
    >
      <DialogTitle id="recirculation-cycle-dialog-title">Ciclo de recirculación</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" className={classes.description}>
          Configuración global: aplica a la recirculación de todos los tanques.
          Durante la recirculación la bomba trabajará el tiempo encendida y descansará
          el tiempo apagada, repitiendo el ciclo.
        </Typography>

        {!isConfigured && (
          <Alert severity="warning" variant="outlined" className={classes.notConfiguredAlert}>
            Todavía no hay un ciclo configurado. Hasta que lo guarde, la bomba de
            recirculación trabajará de forma continua.
          </Alert>
        )}

        <Divider className={classes.divider} />

        <DurationPicker
          idPrefix="recirculation-on"
          label="Tiempo encendida (ON)"
          presets={PRESETS_ON}
          totalMinutes={minutesOn}
          onChange={setMinutesOn}
          disabled={isSending}
          error={showOnError}
        />

        <DurationPicker
          idPrefix="recirculation-off"
          label="Tiempo apagada (OFF)"
          presets={PRESETS_OFF}
          totalMinutes={minutesOff}
          onChange={setMinutesOff}
          disabled={isSending}
          error={showOffError}
        />

        {isValid && (
          <Alert severity="info" variant="outlined" className={classes.preview}>
            La bomba encenderá <strong>{formatDurationFromMinutes(minutesOn)}</strong> y
            descansará <strong>{formatDurationFromMinutes(minutesOff)}</strong>,
            repitiendo el ciclo mientras la recirculación esté activa.
            <br />
            Ciclo completo: {formatDurationFromMinutes(totalCycleMinutes)} — encendida el {dutyCycle} % del tiempo.
            <Typography variant="caption" color="textSecondary" className={classes.previewDetail}>
              Se enviará al módulo como {minutesOn} min ON / {minutesOff} min OFF.
            </Typography>
          </Alert>
        )}

        {status.state !== 'idle' && status.message && (
          <Alert
            severity={
              status.state === 'sending'
                ? 'info'
                : (status.state === 'success' ? 'success' : 'error')
            }
            className={classes.statusAlert}
          >
            {status.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isSending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSending || !isValid}
        >
          {isSending
            ? 'Enviando...'
            : (hasFailed ? 'Reintentar' : (isDirty ? 'Guardar' : 'Reenviar al módulo'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecirculationCycleDialog;